<template>
    <div class="v-table__overflow">
        <table class="v-datatable v-table theme--light">
            <thead>
                <tr>
                    <template v-for="(header) in headers">
                        <th :key="header.value"
                            :class="`column sortable text-xs-left px-1 ${ sortBy == header.value ? `active ${sortAscending ? `asc` : `desc`}` : ``}`"
                            @click="toggleSort(header)">
                            <span class="font-weight-bold">{{ header.text }}</span>
                            <v-icon>arrow_upward</v-icon>
                        </th>
                    </template>
                </tr>
            </thead>
            <tbody>
                <template v-for="(item) in sortedItems">
                    <tr :key="item.key">
                        <td class="px-1">{{ item.Date.format("DD/MM/YYYY") }}</td>
                        <td class="px-1">{{ item.TeamName }}</td>
                        <td class="px-1">{{ item.ActivityName }}</td>
                        <td class="px-1">{{ item.ActivityCode }}</td>
                        <td class="px-1">{{ item.ActivityDepartmentCode }}</td>
                        <td class="px-1">{{ item.ContractorOrganisationName }}</td>
                        <td class="px-1">{{ item.PayTypeName }}</td>
                        <td class="px-1">{{ item.PieceRate | formatNumberTo2Decimals }}</td>
                        <td class="px-1">{{ item.SupervisorHours | formatNumberTo2Decimals }}</td>
                        <td class="px-1">{{ item.AssistantHours | formatNumberTo2Decimals }}</td>
                        <td class="px-1">{{ item.WorkerHours | formatNumberTo2Decimals }}</td>
                        <td class="px-1">{{ item.BreakHours | formatNumberTo2Decimals }}</td>
                        <td class="px-1">{{ item.Units | formatNumberTo0Decimals }}</td>
                        <td class="px-1">{{ item.UnitOfMeasure }}</td>
                        <td class="px-1">{{ item.NumberOfWorkers }}</td>
                    </tr>
                </template>
            </tbody>
            <thead>
                <tr>
                    <th class="column text-xs-left px-1" colspan="8">
                        <span class="font-weight-bold">Totals</span>
                    </th>
                    <th class="column text-xs-left px-1">
                        <span class="font-weight-bold" style="white-space: break-spaces;">Total Supervisor Hours</span>
                    </th>
                    <th class="column text-xs-left px-1">
                        <span class="font-weight-bold" style="white-space: break-spaces;">Total Assistant Hours</span>
                    </th>
                    <th class="column text-xs-left px-1">
                        <span class="font-weight-bold" style="white-space: break-spaces;">Total Worker Hours</span>
                    </th>
                    <th class="column text-xs-left px-1">
                        <span class="font-weight-bold" style="white-space: break-spaces;">Total Break Hours</span>
                    </th>
                    <th class="column text-xs-left px-1" colspan="2">
                        <span class="font-weight-bold" style="white-space: break-spaces;">Total Units</span>
                    </th>
                    <th class="column text-xs-left px-1" colspan="1"></th>
                </tr>
            </thead>
            <tbody>
                <template v-for="(unit, i) in Object.keys(totals.Units)">
                    <tr :key="unit">
                        <template v-if="i == 0">
                            <td class="px-1" colspan="8"></td>
                            <td class="px-1">{{ totals.SupervisorHours | formatNumberTo2Decimals }}</td>
                            <td class="px-1">{{ totals.AssistantHours | formatNumberTo2Decimals }}</td>
                            <td class="px-1">{{ totals.WorkerHours | formatNumberTo2Decimals }}</td>
                            <td class="px-1">{{ totals.BreakHours | formatNumberTo2Decimals }}</td>
                            <td class="px-1">{{ totals.Units[unit] | formatNumberTo0Decimals }}</td>
                            <td class="px-1">{{ unit }}</td>
                            <td class="px-1" colspan="1"></td>
                        </template>
                        <template v-else>
                            <td class="px-1" colspan="12"></td>
                            <td class="px-1">{{ totals.Units[unit] | formatNumberTo0Decimals }}</td>
                            <td class="px-1">{{ unit }}</td>
                            <td class="px-1" colspan="1"></td>
                        </template>
                    </tr>
                </template>
            </tbody>
        </table>
    </div>
</template>

<script>
import moment from "moment";

export default {
	props: {
		previewData: {
			type: Array,
			required: true
		}
	},
	data(){
		return {
            sortBy: null,
            sortAscending: true
		}
	},
	computed: {
        headers(){
            let headers = [];

            headers.push({
                text: "Date",
                value: "Date"
            });

            headers.push({
                text: "Team",
                value: "TeamName"
            });

            headers.push({
                text: "Act.",
                value: "ActivityName"
            });

            headers.push({
                text: "Act. Code",
                value: "ActivityCode"
            });

            headers.push({
                text: "Act. Dept. Code",
                value: "ActivityDepartmentCode"
            });

            headers.push({
                text: "Org.",
                value: "ContractorOrganisationName"
            });

            headers.push({
                text: "Pay Type",
                value: "PayTypeName"
            });

            headers.push({
                text: "Piece Rate",
                value: "PieceRate"
            });

            headers.push({
                text: "Sup. Hours",
                value: "SupervisorHours"
            });

            headers.push({
                text: "Ast. Hours",
                value: "AssistantHours"
            });

            headers.push({
                text: "Wkr. Hours",
                value: "WorkerHours"
            });

            headers.push({
                text: "Break/Move",
                value: "BreakHours"
            });

            headers.push({
                text: "Units",
                value: "Units"
            });

            headers.push({
                text: "UoM",
                value: "UnitOfMeasure"
            });

            headers.push({
                text: "# Workers",
                value: "NumberOfWorkers"
            });

            return headers;
        },
		mappedItems(){
			return this.previewData.map((x, i) => {
				return {
					...x,
					Date: moment(x.Date),
                    key: i
				}
			})
		},
        sortedItems(){
            return [...this.mappedItems].sort((a, b) => {
                // The sort logic is written to be the same as the default vuetify data-table sorting
                let sortBy = this.sortBy;
                let sortAscending = this.sortAscending;
                if(sortBy == null){
                    sortBy = this.headers[0].value;
                    sortAscending = true;
                }

                let sortA = a[sortBy];
                let sortB = b[sortBy];

                if (!sortAscending) {
                    [sortA, sortB] = [sortB, sortA];
                }

                // Check if both are numbers
                if (!isNaN(sortA) && !isNaN(sortB)) {
                    return sortA - sortB;
                }

                // Check if both cannot be evaluated
                if (sortA === null && sortB === null) {
                    return 0;
                }

                [sortA, sortB] = [sortA, sortB].map(
                    s => (s || '').toString().toLocaleLowerCase()
                );

                if (sortA > sortB) return 1;
                if (sortA < sortB) return -1;

                return 0;
            });
        },
        totals(){
            return {
                SupervisorHours: this.mappedItems.reduce((a, v) => a + v.SupervisorHours, 0),
                AssistantHours: this.mappedItems.reduce((a, v) => a + v.AssistantHours, 0),
                WorkerHours: this.mappedItems.reduce((a, v) => a + v.WorkerHours, 0),
                BreakHours: this.mappedItems.reduce((a, v) => a + v.BreakHours, 0),
                Units: this.mappedItems.reduce((a, v) => {
                    if(a[v.UnitOfMeasure] == null){
                        a[v.UnitOfMeasure] = 0;
                    }

                    a[v.UnitOfMeasure] += v.Units;

                    return a;
                }, {})
            }
        }
	},
    methods: {
        toggleSort(header){
            // 3 step toggle
            if(this.sortBy != header.value){
                // Set up sort
                this.sortBy = header.value;
                this.sortAscending = true;
            } else if(this.sortAscending){
                // Toggle to descending
                this.sortAscending = false;
            } else {
                // Remove sort
                this.sortBy = null;
            }
        }
    }
}
</script>

<style scoped>
.reduce-padding {
    padding: 0 8px;
}
</style>